import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { persistAttributionCookie } from '../lib/attribution';
import { authClient } from './client';

export type CheckResult = { status: 'register' | 'email' | 'password' | 'sso', loginUrl?: string };

export type SocialProvider = 'google' | 'github' | 'slack' | 'microsoft';

// Helper function to extract boardId from route or query param
function extractBoardId(path: string): string | null {
    // Try to get from path
    const match = path.match(/^\/(BO\w+)/);
    if (match) {
        return match[1];
    }
    // Try query string
    const params = new URLSearchParams(window.location.search);
    const paramBoardId = params.get('boardId');
    if (paramBoardId && /^BO\w+/.test(paramBoardId)) {
        return paramBoardId;
    }
    return null;
}

export const useCallbackUrl = () => {
    const [path] = useLocation();

    return useMemo(() => {

        const boardId = extractBoardId(path);
        if (boardId) {
            return `${window.location.origin}/${boardId}/join`;
        }

        // If the current URL has a "source", "returnUrl" or "callbackUrl" param, then use that (as absolute)
        // Otherwise, use /app
        const params = new URLSearchParams(window.location.search);
        const paramPath =
            params.get('source') ||
            params.get('returnUrl') ||
            params.get('callbackUrl');

        if (paramPath) {
            // If already absolute, return as is, otherwise prefix with origin
            if (/^https?:\/\//.test(paramPath)) {
                return paramPath;
            }
            return `${window.location.origin}${paramPath.startsWith('/') ? '' : '/'}${paramPath}`;
        }
        return `${window.location.origin}/app`;
    }, [path]);
};

export const useCheckEmailMutation = () =>
    useMutation({
        mutationFn: async (params: { email: string, source: string }): Promise<CheckResult> => {
            const res = await fetch('/login/check', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
            if (!res.ok) throw new Error(`Status ${res.status}`);
            return res.json();
        },
    });

export const useSendMagicLinkMutation = () => {
    const callbackURL = useCallbackUrl();
    return useMutation({
        mutationFn: async ({ email, name }: { email: string, name?: string }) => {
            const res = await authClient.signIn.magicLink({
                email,
                name,
                callbackURL,
                errorCallbackURL: '/login',
            });
            if (res.error) throw new Error(res.error.message ?? 'Could not send magic link');
            return res;
        },
    });
};

// Sends a 6-digit sign-in code to the email. Used by both login and registration; the
// registration flow additionally passes `name` to `verifyOtp` so a brand-new user is created
// with their name (mirroring how magic-link sign-up seeded the Principal).
export const useSendOtpMutation = () =>
    useMutation({
        mutationFn: async ({ email }: { email: string }) => {
            const res = await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
            if (res.error) throw new Error(res.error.message ?? 'Could not send your sign-in code');
            return res;
        },
    });

// Verifies the typed code and, on success, lands the user on the computed callback URL — the
// same destination magic-link verification redirected to (board join, source/returnUrl, or /app).
export const useVerifyOtpMutation = () => {
    const callbackURL = useCallbackUrl();
    return useMutation({
        mutationFn: async ({ email, otp, name }: { email: string, otp: string, name?: string }) => {
            const res = await authClient.signIn.emailOtp({ email, otp, name });
            if (res.error) {
                // better-auth locks the code after `allowedAttempts` wrong tries (FORBIDDEN /
                // TOO_MANY_ATTEMPTS); steer the user to request a fresh one rather than retry.
                if (res.error.code === 'TOO_MANY_ATTEMPTS') {
                    throw new Error('Too many tries with this code, please request a new code.');
                }
                throw new Error(res.error.message ?? 'That code is expired or incorrect');
            }
            return res;
        },
        onSuccess: () => {
            window.location.href = callbackURL;
        },
    });
};

// Signs in with the user's opt-in password (set from the app's settings) and, on success,
// lands them on the computed callback URL — the same destination the OTP flow redirects to.
export const usePasswordSignInMutation = () => {
    const callbackURL = useCallbackUrl();
    return useMutation({
        mutationFn: async ({ email, password }: { email: string, password: string }) => {
            const res = await authClient.signIn.email({ email, password });
            if (res.error) {
                if (res.error.status === 401 || res.error.code === 'INVALID_EMAIL_OR_PASSWORD') {
                    throw new Error('That password is incorrect, please try again.');
                }
                if (res.error.status === 429) {
                    throw new Error('Too many attempts, please wait a moment and try again.');
                }
                throw new Error(res.error.message ?? 'Could not sign you in');
            }
            return res;
        },
        onSuccess: () => {
            window.location.href = callbackURL;
        },
    });
};

// Sends a 6-digit password-reset code. Same delivery path as the sign-in OTP, but the
// 'forget-password' type keys the code to the reset endpoint (and words the email as a reset).
export const useSendResetOtpMutation = () =>
    useMutation({
        mutationFn: async ({ email }: { email: string }) => {
            const res = await authClient.emailOtp.sendVerificationOtp({ email, type: 'forget-password' });
            if (res.error) throw new Error(res.error.message ?? 'Could not send your reset code');
            return res;
        },
    });

// Verifies the reset code and stores the new password, then signs straight in with it —
// resetPassword does not mint a session, so the chained sign-in makes the flow feel like one
// step and lands on the computed callback URL like every other login.
export const useResetPasswordMutation = () => {
    const callbackURL = useCallbackUrl();
    return useMutation({
        mutationFn: async ({ email, otp, password }: { email: string, otp: string, password: string }) => {
            const res = await authClient.emailOtp.resetPassword({ email, otp, password });
            if (res.error) {
                if (res.error.code === 'TOO_MANY_ATTEMPTS') {
                    throw new Error('Too many tries with this code, please request a new code.');
                }
                if (res.error.code === 'INVALID_OTP' || res.error.code === 'OTP_EXPIRED') {
                    throw new Error('That code is expired or incorrect');
                }
                if (res.error.code === 'PASSWORD_TOO_SHORT') {
                    throw new Error('That password is too short, please pick a longer one.');
                }
                throw new Error(res.error.message ?? 'Could not reset your password');
            }
            const signIn = await authClient.signIn.email({ email, password });
            if (signIn.error) throw new Error(signIn.error.message ?? 'Your password was set, please sign in with it');
            return signIn;
        },
        onSuccess: () => {
            window.location.href = callbackURL;
        },
    });
};

// A page can pass its own return path (e.g. the direct-invite page, which has to reopen its link
// so the invite is consumed); otherwise the provider returns to the computed callback URL.
export const useSocialSignInMutation = (returnPath?: string) => {

    const computedCallbackURL = useCallbackUrl();
    const callbackURL = returnPath
        ? new URL(returnPath, window.location.origin).toString()
        : computedCallbackURL;

    return useMutation({
        mutationFn: async (provider: SocialProvider) => {
            persistAttributionCookie();
            const res = await authClient.signIn.social({
                provider: provider as any,
                callbackURL,
            });
            if (res.error) throw new Error(res.error.message ?? `Could not sign in with ${provider}`);
            return res;
        },
    });
};

